/**
 * Lobby room: the room code as a paper tag (click to copy), faction slots, host-editable
 * settings (read-only for guests), chat, and Ready / Start / Leave.
 *
 * A scenario (`settings.scenario`) fixes the terms and the sides: the settings show its briefing
 * and the terms it sets, each slot in play is one of its sides (slot I the briefed side, the
 * others the enemies of the briefing) with that side's asymmetries, the rest are out of play.
 * The sides are read from `scenarioConfig`, exactly what the server starts the war from.
 *
 * A free setup may pick a real-world theatre (`settings.theatre`): its board sets the size, and
 * slot i plays its nation i — every nation in play, the rest closed; teams stay the host's.
 */
import {
  FACTION_COLORS,
  type FactionSetup,
  loadTheatreMap,
  SCENARIO_IDS,
  type ScenarioId,
  scenarioConfig,
  THEATRES,
  type TheatreId,
  type TheatreNation,
} from '@friction/engine';
import type { RoomSettings, RoomSlot, SlotController } from '@friction/protocol';
import { type ReactNode, useEffect, useMemo, useRef, useState } from 'react';
import { inviteLink } from '../../app/nav';
import { bus } from '../../game/bus';
import { cssColor, lighten } from '../../game/format';
import { useT } from '../../i18n';
import { nationName, theatreTitle } from '../../i18n/names';
import { lobbyStore, useLobby } from '../../net/lobby';
import { localizeServerText } from '../../net/serverText';
import { Button, Chip, cx, Field, Rule, Segmented, Select, Slider, TextInput } from '../common/controls';
import { Icon } from '../common/Icon';
import { Stamp } from '../common/Panel';
import { ReconnectBanner } from '../game/Notices';
import { ErrorBanner } from './Multiplayer';
import {
  DAY_LIMITS,
  dayLimitLabel,
  difficulties,
  mapOptions,
  mapSizeInfo,
  ROMAN,
  seasonOptions,
  sideTraits,
  speeds,
  TECH_PRESET_TECHS,
  teamOptions,
  techPresetOf,
  techPresets,
} from './presets';
import { ScreenFrame } from './ScreenFrame';
import s from './screens.module.css';
import { TheatrePreview } from './TheatrePreview';

/** The sides of the room's scenario (as the server will start them), or null for a free setup. */
function scenarioSides(settings: RoomSettings): FactionSetup[] | null {
  const id = settings.scenario;
  return id && SCENARIO_IDS.includes(id) ? scenarioConfig(id, settings.seed).factions : null;
}

/** The nations of the room's real-world theatre (scenario or free setup), or null. */
function theatreNations(settings: RoomSettings): readonly TheatreNation[] | null {
  return settings.theatre ? THEATRES[settings.theatre].nations : null;
}

const pad = (n: number) => String(n).padStart(2, '0');
const clock = (time: number) => {
  const d = new Date(time);
  return `${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

function RoomCode({ code }: { code: string }) {
  const t = useT();
  const [copied, setCopied] = useState(false);
  useEffect(() => {
    if (!copied) return;
    const timer = window.setTimeout(() => setCopied(false), 1600);
    return () => clearTimeout(timer);
  }, [copied]);
  return (
    <button
      type="button"
      className={s.codeBox}
      title={t('lobby.code.copy')}
      onClick={() => {
        void navigator.clipboard?.writeText(code).catch(() => {});
        setCopied(true);
        bus.emit('sound', { name: 'stamp' });
      }}
    >
      <span className={s.codeLabel}>{t('lobby.code.label')}</span>
      <span className={s.codeLetters}>{code}</span>
      <Icon name="copy" size={16} />
      {copied && (
        <Stamp className={s.copied} rotate={8} animate delay={0}>
          {t('lobby.code.copied')}
        </Stamp>
      )}
    </button>
  );
}

/** Copies the room's invite link (opens the game and joins this room). */
function InviteButton({ code }: { code: string }) {
  const t = useT();
  const [copied, setCopied] = useState(false);
  useEffect(() => {
    if (!copied) return;
    const timer = window.setTimeout(() => setCopied(false), 1600);
    return () => clearTimeout(timer);
  }, [copied]);
  return (
    <Button
      variant="secondary"
      icon={copied ? 'check' : 'copy'}
      title={inviteLink(code)}
      onClick={() => {
        void navigator.clipboard?.writeText(inviteLink(code)).catch(() => {});
        setCopied(true);
        bus.emit('sound', { name: 'stamp' });
      }}
    >
      {copied ? t('lobby.invite.copied') : t('lobby.invite')}
    </Button>
  );
}

function SlotRow({
  slot,
  isHost,
  me,
  hostId,
  sides,
  nations,
}: {
  slot: RoomSlot;
  isHost: boolean;
  me: string | null;
  hostId: string;
  /** The scenario's sides, or null for a free setup. */
  sides: FactionSetup[] | null;
  /** The real-world theatre's nations (slot i plays nation i), or null on a generated map. */
  nations: readonly TheatreNation[] | null;
}) {
  const t = useT();
  const lobby = lobbyStore.getState();
  const levels = difficulties(t);
  // In a scenario every side is played (no "closed"), and slots beyond its sides stay out of play.
  // On a real-world theatre likewise: every nation is played, slots beyond its nations are out.
  const side = sides ? (sides[slot.index] ?? null) : undefined;
  const nation = nations ? (nations[slot.index] ?? null) : undefined;
  const outside = side === null || nation === null;
  const controllers: { value: SlotController; label: string }[] = [
    { value: 'open', label: t('lobby.ctl.open') },
    { value: 'ai', label: t('lobby.ctl.ai') },
    ...(sides || nations ? [] : [{ value: 'closed' as const, label: t('lobby.ctl.closed') }]),
  ];
  const mine = slot.playerId !== null && slot.playerId === me;
  const human = slot.controller === 'human';
  const canEdit = isHost || mine;
  const nextColor = () => {
    const i = FACTION_COLORS.indexOf(slot.color as (typeof FACTION_COLORS)[number]);
    lobby.setSlot(slot.index, { color: FACTION_COLORS[(i + 1) % FACTION_COLORS.length] });
  };

  let who: ReactNode;
  if (human)
    who = (
      <span className={s.bellName}>
        <span className={cx(s.bellTitle, mine && s.you)}>
          {slot.playerName ?? t('lobby.commander')}
          {slot.playerId === hostId && <span className={s.host}>{t('lobby.host')}</span>}
        </span>
        <span className={s.bellSub}>{slot.connected ? t('lobby.human') : t('lobby.dropped')}</span>
      </span>
    );
  else if (slot.controller === 'ai')
    who = (
      <span className={s.bellName}>
        <span className={s.bellTitle}>{t('lobby.computer')}</span>
        <span className={s.bellSub}>{levels.find((d) => d.value === slot.aiDifficulty)?.title}</span>
      </span>
    );
  else
    who = (
      <span className={s.bellName}>
        <span className={s.bellTitle} style={{ color: 'var(--muted)' }}>
          {slot.controller === 'open' ? t('lobby.openSlot') : t('lobby.closedSlot')}
        </span>
        <span className={s.bellSub}>
          {slot.controller === 'open'
            ? t('lobby.waitingCommander')
            : outside
              ? t('lobby.scn.outside')
              : t('lobby.notInPlay')}
        </span>
      </span>
    );

  return (
    <div className={cx(s.slot, mine && s.slotMine, slot.controller === 'closed' && s.slotClosed)}>
      <span className={s.slotIdx}>{ROMAN[slot.index]}</span>
      <span
        className={cx(s.swatch, canEdit && s.swatchBtn)}
        style={{ background: cssColor(slot.color) }}
        title={canEdit ? t('lobby.changeColour') : undefined}
        onClick={canEdit ? nextColor : undefined}
      />
      {who}
      <span>
        {human ? (
          mine || !isHost ? null : (
            <Chip>{t('lobby.player')}</Chip>
          )
        ) : isHost && !outside ? (
          <Select
            small
            value={slot.controller}
            options={controllers}
            onChange={(v) => lobby.setSlot(slot.index, { controller: v })}
          />
        ) : slot.controller === 'open' ? (
          <Button size="sm" onClick={() => lobby.takeSlot(slot.index)}>
            {t('lobby.takeSlot')}
          </Button>
        ) : null}
      </span>
      <span>
        {slot.controller === 'ai' &&
          (isHost ? (
            <Segmented
              small
              block
              value={slot.aiDifficulty}
              onChange={(v) => lobby.setSlot(slot.index, { aiDifficulty: v })}
              options={levels.map((d) => ({ ...d, label: d.label[0] }))}
            />
          ) : (
            <span className={s.bellSub}>{t(`difficulty.${slot.aiDifficulty}`)}</span>
          ))}
        {isHost && slot.controller === 'open' && !mine && (
          <Button
            size="sm"
            variant="ghost"
            onClick={() => lobby.takeSlot(slot.index)}
            title={t('lobby.moveHere')}
          >
            {t('lobby.take')}
          </Button>
        )}
      </span>
      <span>
        {slot.controller !== 'closed' && slot.controller !== 'open' && (
          <Select
            small
            value={slot.team}
            options={teamOptions(t)}
            disabled={!canEdit || !!sides}
            title={sides ? t('lobby.scn.ffa') : undefined}
            onChange={(v) => lobby.setSlot(slot.index, { team: v })}
          />
        )}
      </span>
      <span>
        {human ? (
          slot.ready ? (
            <span className={s.ready} style={{ color: 'var(--ok-hi)' }}>
              <Icon name="check" size={14} /> {t('lobby.ready')}
            </span>
          ) : (
            <span className={s.ready} style={{ color: 'var(--muted)' }}>
              <Icon name="hourglass" size={14} /> {t('lobby.waiting')}
            </span>
          )
        ) : slot.controller === 'ai' ? (
          <span className={s.ready} style={{ color: 'var(--muted)' }}>
            <Icon name="gear" size={14} /> {t('lobby.standingBy')}
          </span>
        ) : null}
      </span>
      {side ? (
        <SideLine index={slot.index} side={side} nation={nation ? nationName(nation, t.locale) : null} />
      ) : (
        nation && (
          <div className={s.slotSide}>
            <span className={s.sideRole}>{nationName(nation, t.locale)}</span>
          </div>
        )
      )}
    </div>
  );
}

/** A scenario side under its slot: whose side it is in the briefing, and what sets it apart. */
function SideLine({ index, side, nation }: { index: number; side: FactionSetup; nation: string | null }) {
  const t = useT();
  const traits = sideTraits(t, side);
  const role = index === 0 ? t('lobby.scn.lead') : t('scn.enemy', { num: ROMAN[index - 1] });
  return (
    <div className={s.slotSide} title={index === 0 ? t('lobby.scn.leadHint') : undefined}>
      <span className={cx(s.sideRole, index === 0 && s.sideLead)}>
        {nation ? `${nation} · ${role}` : role}
      </span>
      <span className={s.sideTraits}>{traits.length ? traits.join(' · ') : t('scn.nothingSpecial')}</span>
    </div>
  );
}

function SettingsPanel({ settings, editable }: { settings: RoomSettings; editable: boolean }) {
  const t = useT();
  const set = (p: Partial<RoomSettings>) => lobbyStore.getState().updateSettings(p);
  const [name, setName] = useState(settings.name);
  useEffect(() => setName(settings.name), [settings.name]);
  const sizes = mapSizeInfo(t);
  const tempo = speeds(t);
  const seasons = seasonOptions(t);
  const presets = techPresets(t);
  const ro = (v: string) => <div className={s.ro}>{v}</div>;
  const size = sizes.find((m) => m.value === settings.mapSize);
  const pct = t.pct(settings.vpVictoryShare);
  const scenario = settings.scenario;
  const theatre = settings.theatre ? THEATRES[settings.theatre] : null;
  // A scenario sets the terms: what it fixes is shown, not edited (seed and speed stay free).
  const free = editable && !scenario;
  const fixed = scenario ? t('lobby.scenario.fixed') : undefined;
  const mapLabel = theatre ? theatreTitle(theatre.name, t.locale) : t('sp.map.generated');
  const theatreMap = useMemo(() => (theatre ? loadTheatreMap(theatre.id) : null), [theatre]);
  return (
    <section className={s.section}>
      <Rule>{editable ? t('lobby.rules') : t('lobby.rulesByHost')}</Rule>
      <Field label={t('lobby.scenario')} hint={scenario ? undefined : t('lobby.scenario.hint')}>
        {editable ? (
          <Select<ScenarioId | ''>
            value={scenario ?? ''}
            options={[
              { value: '', label: t('lobby.scenario.none') },
              ...SCENARIO_IDS.map((id) => ({ value: id, label: t(`scn.${id}.title`) })),
            ]}
            onChange={(v) => set({ scenario: v || null })}
          />
        ) : (
          ro(scenario ? t(`scn.${scenario}.title`) : t('lobby.scenario.none'))
        )}
      </Field>
      {scenario && (
        <div className={s.scnBrief}>
          <div className={s.scnInspired}>{t('scn.inspired', { what: t(`scn.${scenario}.inspired`) })}</div>
          <p className={s.scnText}>{t(`scn.${scenario}.brief`)}</p>
          <div className={s.scnLesson}>{t(`scn.${scenario}.lesson`)}</div>
        </div>
      )}
      <Field label={t('sp.map')} hint={scenario ? fixed : t('sp.map.hint')}>
        {free ? (
          <Select<TheatreId | ''>
            value={settings.theatre ?? ''}
            options={mapOptions(t).map((o) => ({
              ...o,
              value: o.value === 'generated' ? ('' as const) : o.value,
            }))}
            onChange={(v) => set({ theatre: v || null })}
          />
        ) : (
          ro(mapLabel)
        )}
      </Field>
      {theatre && (
        <div className={s.theatreCard}>
          <TheatrePreview id={theatre.id} width={160} height={124} label={t('sp.map.preview')} />
          <div className={s.theatreText}>
            <span className={s.sizeName}>{mapLabel}</span>
            <span className={s.note}>{t(`theatre.${theatre.id}.desc`)}</span>
            <span className={s.note}>{t('sp.map.neutral')}</span>
          </div>
        </div>
      )}
      <Field label={t('lobby.roomName')}>
        {editable ? (
          <TextInput
            value={name}
            maxLength={40}
            onChange={setName}
            onBlur={() => name.trim() && name !== settings.name && set({ name: name.trim() })}
          />
        ) : (
          ro(settings.name)
        )}
      </Field>
      <div className={s.grid2}>
        <Field label={t('lobby.theatre')} hint={fixed}>
          {free && !theatre ? (
            <Select
              value={settings.mapSize}
              options={sizes.map((m) => ({ value: m.value, label: `${m.label} · ${m.meta}` }))}
              onChange={(v) => set({ mapSize: v })}
            />
          ) : (
            ro(
              theatreMap
                ? `${theatreMap.width} × ${theatreMap.height} · ${t.num(theatreMap.hexKm ?? 10, 0)} km`
                : `${size?.label} · ${size?.meta}`,
            )
          )}
        </Field>
        <Field label={t('lobby.seed')}>
          {editable ? (
            <TextInput
              mono
              value={settings.seed ? String(settings.seed) : ''}
              placeholder={t('ui.random')}
              onChange={(v) => set({ seed: Math.min(0x7fffffff, Number(v.replace(/\D/g, '')) || 0) })}
            />
          ) : (
            ro(settings.seed ? String(settings.seed) : t('ui.random'))
          )}
        </Field>
        <Field label={t('lobby.dayLimit')} hint={fixed}>
          {free ? (
            <Select
              value={settings.dayLimit}
              options={DAY_LIMITS.map((d) => ({ value: d, label: dayLimitLabel(t, d) }))}
              onChange={(v) => set({ dayLimit: v })}
            />
          ) : (
            ro(dayLimitLabel(t, settings.dayLimit))
          )}
        </Field>
        <Field label={t('lobby.speed')}>
          {editable ? (
            <Segmented block value={settings.speed} options={tempo} onChange={(v) => set({ speed: v })} />
          ) : (
            ro(t('speed.label', { v: tempo.find((x) => x.value === settings.speed)?.label ?? '' }))
          )}
        </Field>
      </div>
      <Field label={t('lobby.decisive')} hint={fixed}>
        {free ? (
          <div className={s.row}>
            <Slider
              value={settings.vpVictoryShare}
              min={0.5}
              max={1}
              step={0.05}
              onChange={(v) => set({ vpVictoryShare: v })}
              label={t('sp.decisive.aria')}
            />
            <span className={s.ro} style={{ width: 150, flex: 'none' }}>
              {t('lobby.decisiveVal', { pct })}
            </span>
          </div>
        ) : (
          ro(t('lobby.decisiveRo', { pct }))
        )}
      </Field>
      <Field
        label={t('lobby.season')}
        hint={fixed ?? seasons.find((x) => x.value === settings.season)?.title}
      >
        {free ? (
          <Segmented block value={settings.season} options={seasons} onChange={(v) => set({ season: v })} />
        ) : (
          ro(seasons.find((x) => x.value === settings.season)?.label ?? t('season.random'))
        )}
      </Field>
      <Field label={t('lobby.techs')} hint={fixed}>
        {free ? (
          <Segmented
            block
            value={techPresetOf(settings.startingTechs)}
            options={presets}
            onChange={(v) => set({ startingTechs: TECH_PRESET_TECHS[v] })}
          />
        ) : (
          ro(presets.find((x) => x.value === techPresetOf(settings.startingTechs))?.label ?? '')
        )}
      </Field>
    </section>
  );
}

function Chat() {
  const t = useT();
  const chat = useLobby((l) => l.chat);
  const [text, setText] = useState('');
  const logRef = useRef<HTMLDivElement>(null);
  // biome-ignore lint/correctness/useExhaustiveDependencies: scroll to the newest line whenever the log grows
  useEffect(() => {
    const el = logRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [chat]);
  const send = () => {
    const msg = text.trim();
    if (!msg) return;
    lobbyStore.getState().sendChat(msg);
    setText('');
  };
  return (
    <div className={s.chat}>
      <div className={s.chatLog} ref={logRef}>
        {chat.map((line, i) => (
          // biome-ignore lint/suspicious/noArrayIndexKey: append-only log without ids; lines never reorder
          <div key={i} className={cx(s.chatLine, line.color === null && s.chatSystem)}>
            <span className={s.chatTime}>{clock(line.time)}</span>
            {line.color !== null && (
              <span className={s.chatFrom} style={{ color: cssColor(lighten(line.color, 0.35)) }}>
                {line.from}
              </span>
            )}
            {/* The server words its own lines in English; players' lines print as typed. */}
            {line.color === null ? localizeServerText(line.text, t) : line.text}
          </div>
        ))}
      </div>
      <div className={s.chatInput}>
        <TextInput
          value={text}
          maxLength={200}
          placeholder={t('lobby.chat.placeholder')}
          onChange={setText}
          onKeyDown={(e) => e.key === 'Enter' && send()}
        />
        <Button icon="courier" disabled={!text.trim()} onClick={send}>
          {t('lobby.chat.send')}
        </Button>
      </div>
    </div>
  );
}

export function LobbyRoom() {
  const t = useT();
  const room = useLobby((l) => l.room);
  const me = useLobby((l) => l.playerId);
  const ping = useLobby((l) => l.ping);
  if (!room) return null;
  const isHost = room.hostId === me;
  const lobby = lobbyStore.getState();
  const sides = scenarioSides(room.settings);
  const nations = theatreNations(room.settings);
  const mySlot = room.slots.find((x) => x.playerId === me);
  const humans = room.slots.filter((x) => x.controller === 'human');
  // A scenario plays all its sides (an open one goes to the computer at the start).
  const active = sides
    ? room.slots.slice(0, sides.length)
    : nations
      ? room.slots.slice(0, nations.length)
      : room.slots.filter((x) => x.controller === 'human' || x.controller === 'ai');
  const allReady = humans.every((x) => x.ready || x.playerId === room.hostId);
  const teams = new Set(active.map((x) => x.team));
  const canStart = isHost && active.length >= 2 && allReady && teams.size >= 2;
  const blocker =
    active.length < 2
      ? t('lobby.needTwo')
      : teams.size < 2
        ? t('lobby.oneTeam')
        : !allReady
          ? t('lobby.notReady')
          : null;

  return (
    <ScreenFrame
      kicker={
        ping !== null
          ? t('lobby.kickerPing', { n: active.length, ping })
          : t('lobby.kicker', { n: active.length })
      }
      title={room.settings.name}
      onBack={() => lobby.leaveRoom()}
      backLabel={t('lobby.leave')}
      right={
        <div className={s.roomCode}>
          <RoomCode code={room.code} />
        </div>
      }
      footer={
        <>
          <span className={s.note}>{isHost ? t('lobby.hostNote') : t('lobby.guestNote')}</span>
          <span className={s.spacer} />
          <InviteButton code={room.code} />
          {blocker && isHost && <span className={s.warn}>{blocker}</span>}
          {!isHost && mySlot && (
            <Button
              variant={mySlot.ready ? 'secondary' : 'primary'}
              icon={mySlot.ready ? 'close' : 'check'}
              onClick={() => lobby.setReady(!mySlot.ready)}
            >
              {mySlot.ready ? t('lobby.notReadyBtn') : t('lobby.reportReady')}
            </Button>
          )}
          {isHost && (
            <Button
              variant="primary"
              size="lg"
              icon="flag"
              disabled={!canStart}
              onClick={() => lobby.startGame()}
            >
              {t('lobby.start')}
            </Button>
          )}
        </>
      }
    >
      <div className={s.stack}>
        <ReconnectBanner />
        <ErrorBanner />
        <div className={s.cols} style={{ gridTemplateColumns: 'minmax(0, 1fr) 400px' }}>
          <div className={s.stack}>
            <section className={s.section}>
              <Rule>{t('lobby.belligerents')}</Rule>
              <div className={s.slots}>
                {room.slots.map((slot) => (
                  <SlotRow
                    key={slot.index}
                    slot={slot}
                    isHost={isHost}
                    me={me}
                    hostId={room.hostId}
                    sides={sides}
                    nations={nations}
                  />
                ))}
              </div>
            </section>
            <section className={s.section} style={{ flex: 1 }}>
              <Rule>{t('lobby.chatter')}</Rule>
              <Chat />
            </section>
          </div>
          <aside>
            <SettingsPanel settings={room.settings} editable={isHost} />
          </aside>
        </div>
      </div>
    </ScreenFrame>
  );
}
